import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Badge,
  Box,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Popover,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { memo, useMemo, useState, type SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { NotificationSeverityChip } from '@/components/notifications';
import { useNotifications } from '@/notifications';

interface NotificationTrayProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
}

type NotificationTab = 'all' | 'unread';

function toRelativeTime(isoValue: string, t: TFunction): string {
  const createdAt = new Date(isoValue).getTime();
  const diffMinutes = Math.floor((Date.now() - createdAt) / 60_000);

  if (diffMinutes < 1) {
    return t('notification.notificationJustNow');
  }

  if (diffMinutes < 60) {
    return t('notification.notificationMinutesAgo', { count: diffMinutes });
  }

  const diffHours = Math.floor(diffMinutes / 60);

  if (diffHours < 24) {
    return t('notification.notificationHoursAgo', { count: diffHours });
  }

  const diffDays = Math.floor(diffHours / 24);
  return t('notification.notificationDaysAgo', { count: diffDays });
}

export const NotificationTray = memo(function NotificationTray({
  anchorEl,
  onClose,
}: NotificationTrayProps) {
  const { t } = useTranslation('entity-portal');
  const {
    notifications,
    unreadCount,
    markRead,
    markAllRead,
    archive,
    deleteMany,
  } = useNotifications();
  const [activeTab, setActiveTab] = useState<NotificationTab>('all');
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);

  const previewNotifications = useMemo(
    () => notifications.slice(0, 10),
    [notifications],
  );

  const visibleNotifications = useMemo(() => {
    if (activeTab === 'unread') {
      return previewNotifications.filter((notification) => !notification.read);
    }

    return previewNotifications;
  }, [activeTab, previewNotifications]);

  const handleTabChange = (_event: SyntheticEvent, value: NotificationTab) => {
    setActiveTab(value);
  };

  return (
    <Popover
      id="notification-tray"
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{
        paper: {
          sx: {
            mt: 0.75,
            width: 420,
            maxWidth: 'calc(100vw - 24px)',
            maxHeight: 560,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 2,
            boxShadow: '0 12px 30px rgba(10, 24, 40, 0.18)',
          },
        },
      }}
    >
      <Box sx={{ px: 2, py: 1.5 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="subtitle1">
            {t('notificationCenter.title')}
          </Typography>

          <Stack direction="row" spacing={0.5} alignItems="center">
            {unreadCount > 0 ? (
              <Typography
                component="button"
                type="button"
                onClick={() => {
                  void markAllRead();
                }}
                sx={{
                  border: 0,
                  bgcolor: 'transparent',
                  color: 'primary.main',
                  cursor: 'pointer',
                  
                }}
              >
                {t('notificationCenter.markAllAsRead')}
              </Typography>
            ) : null}

            <IconButton
              size="small"
              onClick={(event) => setMenuAnchorEl(event.currentTarget)}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>

        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{ mt: 0.5, minHeight: 36 }}
        >
          <Tab
            value="all"
            label={t('notificationCenter.tabAll')}
            sx={{ minHeight: 36 }}
          />
          <Tab
            value="unread"
            sx={{ minHeight: 36 }}
            label={
              <Stack direction="row" spacing={0.75} alignItems="center">
                <span>{t('notificationCenter.tabUnread')}</span>
                {unreadCount > 0 ? (
                  <Badge
                    color="error"
                    badgeContent={unreadCount}
                    sx={{
                      '& .MuiBadge-badge': {
                        position: 'static',
                        transform: 'none',
                      },
                    }}
                  >
                    <Box component="span" sx={{ width: 0, height: 0 }} />
                  </Badge>
                ) : null}
              </Stack>
            }
          />
        </Tabs>
      </Box>

      <Divider />

      {visibleNotifications.length ? (
        <List disablePadding sx={{ overflowY: 'auto', maxHeight: 410 }}>
          {visibleNotifications.map((notification) => (
            <ListItemButton
              key={notification.id}
              onClick={() => {
                if (!notification.read) {
                  void markRead(notification.id);
                }
              }}
              sx={{
                alignItems: 'flex-start',
                py: 1.25,
                px: 2,
                bgcolor: notification.read ? 'transparent' : 'action.hover',
              }}
            >
              <ListItemText
                primary={
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    spacing={1}
                  >
                    <Typography
                      variant={notification.read ? 'subtitle2' : 'subtitle1'}
                    >
                      {notification.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {toRelativeTime(notification.createdAt, t)}
                    </Typography>
                  </Stack>
                }
                secondary={
                  <Stack spacing={0.5} sx={{ mt: 0.25 }}>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {notification.message}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <NotificationSeverityChip
                        severity={notification.severity}
                        compact
                      />
                      <Typography variant="caption" color="text.secondary">
                        {notification.productName}
                      </Typography>
                    </Stack>
                  </Stack>
                }
              />
            </ListItemButton>
          ))}
        </List>
      ) : (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {t('notificationCenter.emptyTitle')}
          </Typography>
        </Box>
      )}

      <Divider />

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={() => setMenuAnchorEl(null)}
      >
        <MenuItem
          onClick={() => {
            void markAllRead();
            setMenuAnchorEl(null);
          }}
        >
          <MarkEmailReadOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
          {t('notificationCenter.markAllAsRead')}
        </MenuItem>
        <MenuItem
          onClick={() => {
            void Promise.all(
              visibleNotifications.map((notification) =>
                archive(notification.id),
              ),
            );
            setMenuAnchorEl(null);
          }}
          disabled={!visibleNotifications.length}
        >
          <ArchiveOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
          {t('notificationCenter.archiveVisible')}
        </MenuItem>
        <MenuItem
          onClick={() => {
            void deleteMany(
              visibleNotifications.map((notification) => notification.id),
            );
            setMenuAnchorEl(null);
          }}
          disabled={!visibleNotifications.length}
        >
          {t('notificationCenter.clearVisible')}
        </MenuItem>
      </Menu>
    </Popover>
  );
});
